import { useState } from 'react';
import { Modal, Skeleton } from '@ai-tutor/ui';

import { InfoPopover } from '@/components/core/common/InfoPopover';
import {
  BlockMarkdown,
  InlineMarkdown,
} from '@/components/core/markdown/markdown';
import { stripMath } from '@/lib/markdown/previewText';
import { FIGURE_SURFACE } from '@/components/core/markdown/diagramTheme';
import { cn } from '@/lib/utils';
import type { Visual } from '@/types/visual';

import { AlreadyEnlargeable, Enlargeable } from './Enlargeable';
import { LabelledDiagram } from './LabelledDiagram';
import { VisualPlaceholder } from './VisualPlaceholder';

// Renders any Visual on the shared figure surface. Every artifact (lesson
// text, highlight card, quiz question, flashcard, Mindspace) shows its visual
// through this one component, so a figure looks the same everywhere.

const DRAWN_FENCE =
  /```(mermaid|chart|vega-lite|vegalite|funcplot|plot|smiles|chem|abc|music|geometry|jsxgraph|geo|venn|numberline|number-line)\b/;

// Tables, maths and code on the white figure surface (fixed dark ink: the
// surface is white in both themes).
const PLAIN_FIGURE =
  'my-0 text-left text-sm text-slate-900 ' +
  '[&_table]:mx-auto [&_table]:border-collapse ' +
  '[&_th]:border-b-2 [&_th]:border-slate-300 [&_th]:px-3 [&_th]:py-1.5 [&_th]:text-left [&_th]:font-semibold ' +
  '[&_td]:border-b [&_td]:border-slate-200 [&_td]:px-3 [&_td]:py-1.5 [&_td]:tabular-nums ' +
  '[&_pre]:rounded-md [&_pre]:bg-slate-50 [&_pre]:p-3 [&_pre]:font-code [&_pre]:text-[13px] ' +
  '[&_blockquote]:border-l-4 [&_blockquote]:border-indigo-300 [&_blockquote]:pl-4 [&_blockquote]:italic ' +
  '[&_.katex-display]:my-2 [&_.katex-display]:text-center [&_ol]:mt-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:my-1';

/** A caption longer than this is cut to two lines; the whole of it sits
 *  behind the info button, with the credit - as a library figure's does. */
const LONG_CAPTION = 120;

function Caption({
  caption,
  credit,
  full = false,
}: {
  caption?: string;
  credit?: string;
  /** In the enlarge dialog: room for all of it. */
  full?: boolean;
}) {
  if (!caption && !credit) return null;
  if (caption && caption.length > LONG_CAPTION && !full) {
    return (
      <figcaption className='mt-2 flex items-start justify-center gap-1 text-xs leading-snug text-slate-600'>
        <span className='line-clamp-2'>
          <InlineMarkdown>{caption}</InlineMarkdown>
        </span>
        <InfoPopover label='Full caption and credit'>
          <p>
            <InlineMarkdown>{caption}</InlineMarkdown>
          </p>
          {credit && <p className='mt-1.5 text-muted-foreground'>{credit}</p>}
        </InfoPopover>
      </figcaption>
    );
  }
  return (
    <figcaption className='mt-2 text-xs leading-snug text-slate-600'>
      {caption && <InlineMarkdown>{caption}</InlineMarkdown>}
      {credit && (
        <span className='mt-0.5 block text-[11px] text-slate-500'>
          {credit}
        </span>
      )}
    </figcaption>
  );
}

function DiagramFigure({
  source,
  caption,
  className,
}: {
  source: string;
  caption?: string;
  className?: string;
}) {
  // A drawn fence brings its own figure surface; a table, equation or code
  // block gets the same white card so every visual reads as one family.
  const drawn = DRAWN_FENCE.test(source);
  return (
    <figure className={cn('my-0', className)} data-visual='diagram'>
      <BlockMarkdown
        className={drawn ? undefined : cn(FIGURE_SURFACE, PLAIN_FIGURE)}
      >
        {source}
      </BlockMarkdown>
      {caption && (
        <figcaption className='-mt-1 text-center text-xs text-muted-foreground'>
          <InlineMarkdown>{caption}</InlineMarkdown>
        </figcaption>
      )}
    </figure>
  );
}

interface VisualBlockProps {
  visual: Visual;
  /** Inside a card (highlight, flashcard): a smaller picture. */
  compact?: boolean;
  /** Drawn big, in the enlarge dialog. */
  large?: boolean;
  /** False on a card with no room for a figure's caption: only its credit
   *  (the licence needs it) is kept. */
  caption?: boolean;
  /** A tap opens it full size (lesson text, chat). Not on a card, where a
   *  click already flips or turns it. */
  enlargeable?: boolean;
  className?: string;
}

export function VisualBlock({
  enlargeable = false,
  ...props
}: VisualBlockProps) {
  if (props.visual.kind === 'pending') {
    return (
      <VisualPlaceholder label={props.visual.alt} className={props.className} />
    );
  }
  if (!enlargeable) return <VisualFigure {...props} />;
  const { visual } = props;
  const label = stripMath(
    (visual.kind === 'diagram' ? visual.caption : visual.alt) || 'Picture'
  );
  return (
    <Enlargeable
      label={label}
      large={<VisualFigure visual={visual} large className='border-0' />}
    >
      <VisualFigure {...props} />
    </Enlargeable>
  );
}

const pictureHeight = (compact: boolean, large: boolean) =>
  large ? 'max-h-[70dvh]' : compact ? 'max-h-56' : 'max-h-72';
const labelledSize = (compact: boolean, large: boolean) =>
  large ? 760 : compact ? 240 : 420;

function VisualFigure({
  visual,
  compact = false,
  large = false,
  caption = true,
  className,
}: Omit<VisualBlockProps, 'enlargeable'>) {
  // A picture that does not load (an expired link, a removed file) takes its
  // figure with it: the surface keeps its layout without it.
  const [failed, setFailed] = useState(false);
  if (failed) return null;
  if (visual.kind === 'pending') {
    return <VisualPlaceholder label={visual.alt} className={className} />;
  }
  if (visual.kind === 'diagram') {
    return (
      <DiagramFigure
        source={visual.source}
        caption={caption ? visual.caption : undefined}
        className={className}
      />
    );
  }
  if (visual.kind === 'image') {
    return (
      <figure
        className={cn(FIGURE_SURFACE, 'my-0 overflow-hidden', className)}
        data-visual='image'
      >
        <img
          src={visual.src}
          alt={stripMath(visual.alt ?? '')}
          loading='lazy'
          onError={() => setFailed(true)}
          className={cn(
            'mx-auto w-auto max-w-full rounded-md',
            pictureHeight(compact, large)
          )}
        />
        <Caption
          caption={caption ? visual.caption : undefined}
          credit={visual.credit}
          full={large}
        />
      </figure>
    );
  }
  return (
    <figure
      className={cn(FIGURE_SURFACE, 'my-0 overflow-hidden', className)}
      data-visual='labelled'
    >
      <LabelledDiagram
        src={visual.src}
        alt={stripMath(visual.alt ?? '')}
        labels={visual.labels}
        view={visual.view}
        maxSize={labelledSize(compact, large)}
        onError={() => setFailed(true)}
      />
      <Caption
        caption={caption ? visual.caption : undefined}
        credit={visual.credit}
        full={large}
      />
    </figure>
  );
}

/** Whether a visual has a thumbnail: a picture does; a drawn diagram does
 *  not - shrunk to a tile, a flowchart or table is unreadable noise. A
 *  placeholder has one when it will be a picture. */
export function hasThumb(visual: Visual | null | undefined): boolean {
  if (!visual || visual.kind === 'diagram') return false;
  return visual.kind !== 'pending' || visual.becomes !== 'diagram';
}

/** A small, square preview of a picture for lists and tiles (Mindspace);
 *  nothing for a drawn diagram (see `hasThumb`). */
export function VisualThumb({
  visual,
  className,
}: {
  visual: Visual;
  className?: string;
}) {
  const [failed, setFailed] = useState(false);
  if (failed || visual.kind === 'diagram') return null;
  if (visual.kind === 'pending') {
    const what = visual.alt ? `Drawing: ${visual.alt}` : 'Drawing the picture';
    return (
      <Skeleton
        role='status'
        aria-label={`${what}, on its way`}
        className={cn('aspect-square rounded-lg', className)}
        data-testid='concept-visual-placeholder'
      />
    );
  }
  return (
    <img
      src={visual.src}
      alt={stripMath(visual.alt ?? '')}
      loading='lazy'
      onError={() => setFailed(true)}
      className={cn(
        'aspect-square rounded-lg border border-border bg-white object-contain',
        className
      )}
    />
  );
}

/**
 * A thumbnail that opens its picture full size, every label shown (the
 * glossary). The whole tile is the button: at this size a corner button
 * would cover the picture. A picture still being drawn is a placeholder
 * tile, not a button.
 */
/** The width a diagram is laid out at before it is shrunk into a thumbnail:
 *  a board or a flowchart cannot lay itself out in 64px, but can at this and
 *  be scaled down as a picture is. */
const DIAGRAM_PREVIEW_WIDTH = 320;

export function EnlargeableThumb({
  visual,
  label,
  className,
  diagram = false,
}: {
  visual: Visual;
  /** What the picture shows: names the button and the dialog. */
  label: string;
  className?: string;
  /** A drawn diagram too, as a shrunk preview that opens full size. Off by
   *  default: at thumbnail size a diagram is a shape, not something to read,
   *  so only a surface that opens it (the glossary) asks for one. */
  diagram?: boolean;
}) {
  const [open, setOpen] = useState(false);
  if (visual.kind === 'diagram' && !diagram) return null;
  if (visual.kind === 'diagram') {
    return (
      <AlreadyEnlargeable>
        <button
          type='button'
          onClick={() => setOpen(true)}
          aria-label={`Enlarge: ${label}`}
          className={cn(
            'relative flex-shrink-0 cursor-zoom-in overflow-hidden rounded-lg border border-border bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            className
          )}
          data-enlargeable
          data-diagram-thumb
        >
          <span
            aria-hidden='true'
            className='pointer-events-none absolute left-1/2 top-1/2 block origin-center'
            style={{
              width: DIAGRAM_PREVIEW_WIDTH,
              transform: 'translate(-50%, -50%) scale(0.2)',
            }}
          >
            <VisualFigure visual={visual} compact caption={false} />
          </span>
        </button>
        <Modal
          isOpen={open}
          onClose={() => setOpen(false)}
          ariaLabel={label}
          size='xl'
        >
          <div className='max-h-[80dvh] overflow-y-auto'>
            <VisualFigure visual={visual} large className='border-0' />
          </div>
        </Modal>
      </AlreadyEnlargeable>
    );
  }
  if (visual.kind === 'pending') {
    return <VisualThumb visual={visual} className={className} />;
  }
  return (
    <>
      <button
        type='button'
        onClick={() => setOpen(true)}
        aria-label={`Enlarge: ${label}`}
        className={cn(
          'flex-shrink-0 cursor-zoom-in rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          className
        )}
        data-enlargeable
      >
        <VisualThumb visual={visual} className='h-full w-full' />
      </button>
      <Modal
        isOpen={open}
        onClose={() => setOpen(false)}
        ariaLabel={label}
        size='xl'
      >
        <div className='max-h-[80dvh] overflow-y-auto'>
          <VisualFigure visual={visual} large className='border-0' />
        </div>
      </Modal>
    </>
  );
}
